import { useEffect, useState } from 'react';
import { BellIcon, Cross2Icon } from '@radix-ui/react-icons';
import { Button, Dialog } from '@radix-ui/themes';
import { isLoggedIn } from 'helpers/authentication';
import { checkFirebaseMessagingSupport, isUnsupportedBrowser } from 'helpers/browserSupport';
import { initPushNotifications } from 'helpers/crmUtils';
import { successToastMessage } from 'helpers/toaster';
import Analytics from 'ui/design-base/Analytics';
import { saveAppToken } from 'store/editProfile/editProfileSlice';
import { fetchNotificationsCounter } from 'store/notifications/notificationsSlice';
import { useAppDispatch } from 'store/store';
import { getFirebaseToken, onForegroundMessage } from '../../firebase';

export const PushNotificationPopup: React.FC = () => {
  const dispatch = useAppDispatch();

  const [showNotificationBanner, setShowNotificationBanner] = useState(false);

  const handleGetFirebaseToken = () => {
    getFirebaseToken()
      .then((firebaseToken) => {
        if (firebaseToken) {
          dispatch(saveAppToken({ token: firebaseToken, os: 'web' }));
          console.warn('Firebase token to send to db: put to app-token-info', firebaseToken);
          setShowNotificationBanner(false);
          Analytics.sendEvent({
            category: 'Firebase',
            action: 'Firebase notifications enabled',
            label: 'Firebase Popup Modal',
          });

          initPushNotifications();
        }
      })
      .catch((err) => {
        console.error('An error occured while retrieving firebase token. ', err);
        if (err.message?.includes('unsupported-browser') || err.message?.includes('not supported')) {
          console.warn('Firebase Messaging is not supported in this browser');
          setShowNotificationBanner(false);
        }
      });
  };

  useEffect(() => {
    onForegroundMessage()
      .then((payload) => {
        if (!payload.notification) return;
        successToastMessage(`${payload.notification.title} - ${payload.notification.body}`);
        setTimeout(() => isLoggedIn() && dispatch(fetchNotificationsCounter()), 2000);
      })
      .catch((err) => {
        if (!err.message?.includes('not supported')) {
          console.error('An error occurred while retrieving foreground message. ', err);
        }
      });
  }, [dispatch]);

  useEffect(() => {
    setTimeout(() => {
      console.warn('Notification permission: ', window.Notification?.permission);

      // Check if browser supports Firebase Messaging
      if (!checkFirebaseMessagingSupport() || isUnsupportedBrowser()) {
        console.warn('Browser does not support Firebase Messaging - notification banner hidden');
        setShowNotificationBanner(false);
        return;
      }

      const alreadyShown = sessionStorage.getItem('alreadyShown');
      setShowNotificationBanner(window.Notification?.permission === 'default' && isLoggedIn() && !alreadyShown);
      if (window.Notification?.permission === 'granted' && isLoggedIn()) {
        initPushNotifications();
      }
    }, 1500);

    //old management with adv evolution
    // const interval = setInterval(() => {
    //   //cookie banner not compatible with modals
    //   if (document.querySelector('.qc-cmp2-container')) {
    //     setShowNotificationBanner(false);
    //   }
    // }, 1000);
    // return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (showNotificationBanner) {
      Analytics.sendEvent({
        category: 'Firebase',
        action: 'Popup Firebase displayed',
        label: 'Firebase Popup Modal',
      });
    }
  }, [showNotificationBanner]);

  const content = (
    <div id='container' className='flex flex-col items-start gap-4 self-stretch p-3 pt-0'>
      <div id='error' className='flex flex-col items-start gap-4 self-stretch'>
        <div className='flex items-center gap-2 self-stretch'>
          <div id='icon' className='flex h-8 w-8 gap-2 fill-transparent'>
            <BellIcon width='32' height='32' color='#E2A336' />
          </div>
          <div className='text-neutral-neutral-12 text-lg leading-6 font-bold'>Abilita notifiche push</div>
        </div>
        <div className='text-neutral-neutral-12 text-sm font-normal'>
          Per avere aggiornamenti in tempo reale su partite e punti, abilita le notifiche push sul tuo browser dal popup
          che ti apparirà
        </div>
      </div>

      <Button size='2' className='w-full cursor-pointer self-stretch' onClick={handleGetFirebaseToken}>
        Cliccando qui
      </Button>
    </div>
  );

  return (
    <Dialog.Root open={showNotificationBanner}>
      <Dialog.Content maxWidth='450px'>
        <div className='flex justify-end'>
          <Dialog.Title className='hidden'>Abilita notifiche push</Dialog.Title>
          <Dialog.Close className='cursor-pointer justify-end'>
            <Cross2Icon
              height={'24'}
              width={'24'}
              onClick={() => {
                setShowNotificationBanner(false);
                sessionStorage.setItem('alreadyShown', 'true');
              }}
            />
          </Dialog.Close>
        </div>
        <Dialog.Description className='hidden'>
          Per avere aggiornamenti in tempo reale su partite e punti, abilita le notifiche push sul tuo browser dal popup
          che ti apparirà
        </Dialog.Description>
        {content}
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default PushNotificationPopup;
